type Fn = (...args: any[]) => any;

type DebouncedFunction<T extends Fn> = {
  (...args: Parameters<T>): void;
};

/**
 * Creates a debounced function that delays invoking the provided function until after
 * a specified wait time has elapsed since the last invocation.
 *
 * @example
 * ```ts
 * const debouncedLog = debounce(console.log, 1000);
 * debouncedLog('Hello'); // Will log after 1 second if not called again
 * debouncedLog('World'); // Resets the timer, will log 'World' after 1 second
 * ```
 *
 * @param fn - The function to debounce.
 * @param delay
 *
 * @returns A debounced function
 */
export function debounce<T extends Fn>(
  fn: Fn,
  delay = 300,
): DebouncedFunction<T> {
  let timeoutFn: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    if (timeoutFn) clearTimeout(timeoutFn);
    timeoutFn = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}
